/**
 * RaceSessionProvider — the single client state boundary for the race workspace.
 *
 * Owns: the session catalog, the selected session and mode (replay|live), the
 * layout, replay assets (manifest, checkpoints, timing/motion/telemetry chunks,
 * events), the live SSE subscription, the playback clock, and the selection
 * (selected/compare drivers, follow). Every panel reads from this provider at
 * the same playback cursor; no panel has its own timer.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import raceEngineApi from "./api";
import { buildProjector } from "./geometry";
import { MotionStore, ReplayMaterializer, applyPatches, stampMaterialized, summarizeTransition } from "./playback";

const RaceSessionContext = createContext(null);
// Per-frame signals (the animation clock and motion sample arrivals) live in their own context so that only the
// components that draw per frame (the map, the inspector HUD) re-render per frame; everything else re-renders when
// the materialized state or the 4 Hz cursor changes.
const RaceFrameContext = createContext(null);

export const CURSOR_DISPATCH_HZ = 4;
export const STATE_PREFETCH_SECONDS = 20;

export const SPEED_OPTIONS = [1, 2, 5, 10];

const initialState = {
    catalog: null,
    catalogError: null,
    sessionKey: null,
    mode: "replay", // replay | live
    status: "idle", // idle | loading | ready | error | empty
    message: null,
    manifest: null,
    layout: null,
    events: [],
    snapshot: null, // merged state at the current cursor (checkpoint + patches)
    snapshotCheckpoint: null,
    live: null, // live status block from the API
    liveStream: "idle", // idle | connecting | open | reconnecting | closed
    sequence: 0,
    gaps: 0,
    selectedEntryId: null,
    compareEntryId: null,
    follow: false,
    playing: false,
    speed: 1,
    cursor: 0, // session seconds
    error: null,
    loadedChunks: [],
    stateLag: null, // seconds the displayed state trails the cursor while its assets load (null when in step)
};

function reducer(state, action) {
    switch (action.type) {
        case "catalog":
            return { ...state, catalog: action.catalog, catalogError: null };
        case "catalog_error":
            return { ...state, catalogError: action.error };
        case "select_session":
            return { ...initialState, catalog: state.catalog, sessionKey: action.sessionKey, mode: action.mode, status: "loading", speed: state.speed };
        case "replay_ready":
            return { ...state, status: "ready", manifest: action.manifest, layout: action.layout, events: action.events, cursor: action.cursor, message: null, error: null };
        case "live_ready":
            return { ...state, status: "ready", layout: action.layout, live: action.live, message: null, error: null };
        case "snapshot":
            return { ...state, snapshot: action.snapshot, snapshotCheckpoint: action.checkpoint ?? state.snapshotCheckpoint, sequence: action.snapshot?.sequence ?? state.sequence, stateLag: action.lag ?? null };
        case "state_lag":
            return state.stateLag === action.lag ? state : { ...state, stateLag: action.lag };
        case "events":
            return { ...state, events: action.events };
        case "live_status":
            return { ...state, live: action.live };
        case "live_stream":
            return { ...state, liveStream: action.liveStream, gaps: action.gaps ?? state.gaps };
        case "status":
            return { ...state, status: action.status, message: action.message ?? state.message, error: action.error ?? null };
        case "cursor":
            return { ...state, cursor: action.cursor };
        case "playing":
            return { ...state, playing: action.playing };
        case "speed":
            return { ...state, speed: action.speed };
        case "select_entry":
            return { ...state, selectedEntryId: action.entryId, follow: action.entryId ? state.follow : false };
        case "compare_entry":
            return { ...state, compareEntryId: action.entryId };
        case "follow":
            return { ...state, follow: action.follow };
        case "chunks":
            return { ...state, loadedChunks: action.chunks };
        default:
            return state;
    }
}

function chunkIndexFor(manifest, seconds) {
    const { origin_seconds: origin, chunk_seconds: chunk } = manifest.time;
    return Math.floor((seconds - origin) / chunk);
}

function checkpointIndexFor(manifest, seconds) {
    const { origin_seconds: origin, checkpoint_seconds: ckpt } = manifest.time;
    return Math.max(0, Math.min(manifest.checkpoints.length - 1, Math.floor((seconds - origin) / ckpt)));
}

export function RaceSessionProvider({ children, initialSessionKey = null, initialMode = "replay", instrument = false }) {
    const [state, dispatch] = useReducer(reducer, initialState);
    const motionStore = useRef(new MotionStore());
    const materializerRef = useRef(null); // synchronous checkpoint+patches materializer for the replay cursor
    const eventTimesRef = useRef([]); // sorted event times: the cursor is re-published the frame an event becomes current
    const playbackLogRef = useRef(null); // developer instrumentation (window.__racePlayback)
    const lastDispatchRef = useRef({ t: null, appliedThroughS: null, snapshot: null });
    const telemetryRef = useRef(new Map()); // entryId -> sorted telemetry rows
    const chunkCache = useRef(new Map()); // key -> promise
    const checkpointCache = useRef(new Map());
    const abortRef = useRef(null);
    const sseRef = useRef(null);
    const clockRef = useRef({ cursor: 0, lastFrame: null });
    const liveBaseRef = useRef(null); // snapshot the live patches merge into
    const [frameTick, setFrameTick] = useState(0);
    const stateRef = useRef(state);
    stateRef.current = state;

    // ---------------------------------------------------------------- catalog
    const loadCatalog = useCallback(async () => {
        try {
            const catalog = await raceEngineApi.sessions();
            dispatch({ type: "catalog", catalog });
            return catalog;
        } catch (error) {
            dispatch({ type: "catalog_error", error });
            return null;
        }
    }, []);

    useEffect(() => {
        loadCatalog();
    }, [loadCatalog]);

    // ----------------------------------------------------------- replay load
    const fetchChunk = useCallback(
        (kind, index) => {
            const manifest = stateRef.current.manifest;
            if (!manifest || index < 0 || index >= manifest.chunks.length) return Promise.resolve(null);
            const key = `${manifest.build_id}:${kind}:${index}`;
            if (!chunkCache.current.has(key)) {
                const path = `${kind}/chunk-${String(index).padStart(4, "0")}.json.gz`;
                const promise = raceEngineApi
                    .replayFile(stateRef.current.sessionKey, manifest.build_id, path)
                    .then((payload) => {
                        if (kind === "motion") {
                            motionStore.current.addChunk(index, payload.entries);
                            setFrameTick((tick) => tick + 1);
                        }
                        if (kind === "timing" && materializerRef.current && materializerRef.current.manifest.build_id === manifest.build_id) {
                            materializerRef.current.setChunk(index, payload);
                            setFrameTick((tick) => tick + 1); // a frame waiting on this chunk can now materialize
                        }
                        if (kind === "telemetry") {
                            Object.entries(payload.entries || {}).forEach(([entryId, rows]) => {
                                const existing = telemetryRef.current.get(entryId) || [];
                                telemetryRef.current.set(entryId, existing.concat(rows).sort((a, b) => a[0] - b[0]));
                            });
                        }
                        return payload;
                    })
                    .catch((error) => {
                        chunkCache.current.delete(key);
                        if (kind === "telemetry" && error?.status === 404) return null;
                        throw error;
                    });
                chunkCache.current.set(key, promise);
            }
            return chunkCache.current.get(key);
        },
        []
    );

    const fetchCheckpoint = useCallback((index) => {
        const manifest = stateRef.current.manifest;
        if (!manifest) return Promise.resolve(null);
        const key = `${manifest.build_id}:${index}`;
        if (!checkpointCache.current.has(key)) {
            const path = `checkpoints/ckpt-${String(index).padStart(4, "0")}.json.gz`;
            const promise = raceEngineApi
                .replayFile(stateRef.current.sessionKey, manifest.build_id, path)
                .then((payload) => {
                    if (materializerRef.current && materializerRef.current.manifest.build_id === manifest.build_id) {
                        materializerRef.current.setCheckpoint(index, payload);
                        setFrameTick((tick) => tick + 1);
                    }
                    return payload;
                })
                .catch((error) => {
                    checkpointCache.current.delete(key);
                    throw error;
                });
            checkpointCache.current.set(key, promise);
        }
        return checkpointCache.current.get(key);
    }, []);

    /** State at session time t: nearest checkpoint at or before t plus the patches up to t. */
    const materialize = useCallback(
        async (t) => {
            const manifest = stateRef.current.manifest;
            if (!manifest) return null;
            const ckIndex = checkpointIndexFor(manifest, t);
            const checkpoint = await fetchCheckpoint(ckIndex);
            const ckSeconds = manifest.checkpoints[ckIndex].session_seconds;
            const first = chunkIndexFor(manifest, ckSeconds);
            const last = chunkIndexFor(manifest, t);
            const patches = [];
            for (let index = first; index <= last; index += 1) {
                // eslint-disable-next-line no-await-in-loop
                const chunk = await fetchChunk("timing", index);
                // Closed on both ends: a checkpoint is taken before the observations stamped exactly at its time are applied.
                if (chunk) chunk.patches.forEach((patch) => patch.s != null && patch.s >= ckSeconds && patch.s <= t && patches.push(patch));
            }
            return { snapshot: applyPatches(checkpoint, patches, t), checkpoint: ckIndex };
        },
        [fetchCheckpoint, fetchChunk]
    );

    /** Fetch (without waiting) the checkpoint and timing chunks the state at t needs. */
    const ensureStateAround = useCallback(
        (t) => {
            const manifest = stateRef.current.manifest;
            if (!manifest) return;
            const clamped = Math.max(manifest.time.origin_seconds, Math.min(manifest.time.end_seconds, t));
            const ckIndex = checkpointIndexFor(manifest, clamped);
            fetchCheckpoint(ckIndex).catch(() => null);
            for (let index = chunkIndexFor(manifest, manifest.checkpoints[ckIndex].session_seconds); index <= chunkIndexFor(manifest, clamped); index += 1) fetchChunk("timing", index).catch(() => null);
        },
        [fetchCheckpoint, fetchChunk]
    );

    /** Publish a materialized state for cursor t (only when it changed, or when asked to). */
    const publishState = useCallback((result, t, { force = false } = {}) => {
        if (!result) return false;
        if (!result.changed && !force) return false;
        const snapshot = stampMaterialized(result.snapshot, t, result.appliedThroughS);
        const log = playbackLogRef.current;
        if (log && lastDispatchRef.current.snapshot && result.changed) {
            const kinds = summarizeTransition(lastDispatchRef.current.snapshot, result.snapshot);
            if (kinds.length) log.transitions.push({ t, appliedThroughS: result.appliedThroughS, kinds, wall: typeof performance !== "undefined" ? performance.now() : Date.now() });
            if (log.transitions.length > 5000) log.transitions.splice(0, log.transitions.length - 5000);
        }
        lastDispatchRef.current = { t, appliedThroughS: result.appliedThroughS, snapshot: result.snapshot };
        dispatch({ type: "snapshot", snapshot, checkpoint: result.checkpoint, lag: null });
        return true;
    }, []);

    const ensureMotionAround = useCallback(
        (t) => {
            const manifest = stateRef.current.manifest;
            if (!manifest) return;
            const index = chunkIndexFor(manifest, t);
            [index, index + 1].forEach((i) => {
                fetchChunk("motion", i).catch(() => null);
                fetchChunk("telemetry", i).catch(() => null);
            });
        },
        [fetchChunk]
    );

    const seekTo = useCallback(
        async (seconds) => {
            const manifest = stateRef.current.manifest;
            if (!manifest) return;
            const t = Math.max(manifest.time.origin_seconds, Math.min(manifest.time.end_seconds, Number(seconds)));
            clockRef.current.cursor = t;
            dispatch({ type: "cursor", cursor: t });
            ensureMotionAround(t);
            ensureStateAround(t);
            ensureStateAround(t + STATE_PREFETCH_SECONDS);
            const sync = materializerRef.current?.stateAt(t);
            if (sync) {
                publishState(sync, t, { force: true });
                setFrameTick((tick) => tick + 1);
                return;
            }
            try {
                const result = await materialize(t);
                if (!result || stateRef.current.manifest?.build_id !== manifest.build_id) return;
                if (Math.abs(clockRef.current.cursor - t) > manifest.time.checkpoint_seconds) return; // superseded by a newer seek
                const again = materializerRef.current?.stateAt(clockRef.current.cursor);
                if (again) publishState(again, clockRef.current.cursor, { force: true });
                else dispatch({ type: "snapshot", snapshot: result.snapshot, checkpoint: result.checkpoint });
                if (stateRef.current.status === "error") dispatch({ type: "status", status: "ready", message: null, error: null });
                setFrameTick((tick) => tick + 1);
            } catch (error) {
                // Requests abandoned because a newer seek superseded them are not failures of this moment.
                if (error?.name === "AbortError") return;
                if (stateRef.current.manifest?.build_id !== manifest.build_id) return;
                if (Math.abs(clockRef.current.cursor - t) > manifest.time.checkpoint_seconds) return;
                dispatch({ type: "status", status: "error", message: "Replay data could not be loaded for this moment.", error });
            }
        },
        [ensureMotionAround, ensureStateAround, materialize, publishState]
    );

    const selectSession = useCallback(
        async (sessionKey, mode = "replay") => {
            if (abortRef.current) abortRef.current.abort();
            if (sseRef.current) {
                sseRef.current.close();
                sseRef.current = null;
            }
            const controller = new AbortController();
            abortRef.current = controller;
            motionStore.current.reset();
            telemetryRef.current = new Map();
            chunkCache.current = new Map();
            checkpointCache.current = new Map();
            liveBaseRef.current = null;
            materializerRef.current = null;
            lastDispatchRef.current = { t: null, appliedThroughS: null, snapshot: null };
            dispatch({ type: "select_session", sessionKey, mode });
            if (mode === "live") {
                await openLive(controller);
                return;
            }
            try {
                const manifest = await raceEngineApi.manifest(sessionKey, { signal: controller.signal });
                if (controller.signal.aborted) return;
                // Layout and events come from the build itself (immutable, addressed by build id) so a
                // rebuild or recalibration can never be masked by a cached copy of an unversioned URL.
                const [layout, eventsPayload] = await Promise.all([
                    raceEngineApi
                        .replayFile(sessionKey, manifest.build_id, "layout.json", { signal: controller.signal })
                        .catch(() => raceEngineApi.layout(sessionKey, { signal: controller.signal }).catch(() => null)),
                    raceEngineApi
                        .replayFile(sessionKey, manifest.build_id, "events.json", { signal: controller.signal })
                        .catch(() => raceEngineApi.events(sessionKey, { signal: controller.signal }).catch(() => ({ events: [] }))),
                ]);
                if (controller.signal.aborted) return;
                const start = manifest.time.race_start_seconds != null ? manifest.time.race_start_seconds - 60 : manifest.time.origin_seconds;
                clockRef.current.cursor = start;
                materializerRef.current = new ReplayMaterializer(manifest);
                dispatch({ type: "replay_ready", manifest, layout, events: eventsPayload.events || [], cursor: start });
                // materialize the first frame
                const result = await (async () => {
                    stateRef.current = { ...stateRef.current, manifest, sessionKey };
                    return materialize(start);
                })();
                if (controller.signal.aborted) return;
                ensureMotionAround(start);
                ensureStateAround(start + STATE_PREFETCH_SECONDS);
                const sync = materializerRef.current?.stateAt(start);
                if (sync) publishState(sync, start, { force: true });
                else if (result) dispatch({ type: "snapshot", snapshot: result.snapshot, checkpoint: result.checkpoint });
            } catch (error) {
                if (controller.signal.aborted) return;
                const unavailable = error?.status === 404;
                dispatch({ type: "status", status: unavailable ? "empty" : "error", message: unavailable ? "No replay has been published for this session yet." : "The replay could not be loaded.", error });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [materialize, ensureMotionAround]
    );

    // ------------------------------------------------------------------ live
    const connectStream = useCallback((controller) => {
        if (typeof window === "undefined" || typeof window.EventSource === "undefined") return;
        const source = new window.EventSource(raceEngineApi.liveStreamUrl(stateRef.current.sequence || null));
        sseRef.current = source;
        dispatch({ type: "live_stream", liveStream: "connecting" });
        source.addEventListener("open", () => dispatch({ type: "live_stream", liveStream: "open" }));
        source.addEventListener("status", (event) => {
            try {
                const live = JSON.parse(event.data);
                dispatch({ type: "live_status", live });
            } catch (error) {
                /* ignore */
            }
        });
        source.addEventListener("snapshot", (event) => {
            try {
                const snapshot = JSON.parse(event.data);
                liveBaseRef.current = snapshot;
                dispatch({ type: "snapshot", snapshot });
                clockRef.current.cursor = snapshot.clock?.session_seconds ?? clockRef.current.cursor;
                dispatch({ type: "cursor", cursor: clockRef.current.cursor });
                motionStore.current.trimBefore(clockRef.current.cursor - 120);
            } catch (error) {
                /* ignore */
            }
        });
        source.addEventListener("patches", (event) => {
            try {
                const { patches } = JSON.parse(event.data);
                const base = liveBaseRef.current;
                if (!base || !patches?.length) return;
                const first = patches[0].seq;
                const expected = (base.sequence || 0) + 1;
                if (base.sequence && first > expected) {
                    dispatch({ type: "live_stream", liveStream: "open", gaps: (stateRef.current.gaps || 0) + 1 });
                    // Missing sequence: resync by reconnecting with `since`.
                    source.close();
                    sseRef.current = null;
                    setTimeout(() => connectStream(controller), 250);
                    return;
                }
                const next = applyPatches(base, patches);
                liveBaseRef.current = next;
                dispatch({ type: "snapshot", snapshot: next });
                if (next.clock?.session_seconds != null) {
                    clockRef.current.cursor = next.clock.session_seconds;
                    dispatch({ type: "cursor", cursor: clockRef.current.cursor });
                }
            } catch (error) {
                /* ignore */
            }
        });
        source.addEventListener("motion", (event) => {
            try {
                motionStore.current.addLiveBatch(JSON.parse(event.data));
                setFrameTick((tick) => tick + 1);
            } catch (error) {
                /* ignore */
            }
        });
        source.addEventListener("events", (event) => {
            try {
                const { events } = JSON.parse(event.data);
                const existing = new Map((stateRef.current.events || []).map((item) => [item.id, item]));
                (events || []).forEach((item) => existing.set(item.id, item));
                dispatch({ type: "events", events: [...existing.values()].sort((a, b) => (a.session_seconds ?? 0) - (b.session_seconds ?? 0)) });
            } catch (error) {
                /* ignore */
            }
        });
        source.addEventListener("heartbeat", () => dispatch({ type: "live_stream", liveStream: "open" }));
        source.onerror = () => {
            dispatch({ type: "live_stream", liveStream: "reconnecting" });
        };
    }, []);

    const openLive = useCallback(async (controller) => {
        dispatch({ type: "live_stream", liveStream: "connecting" });
        let live;
        try {
            live = await raceEngineApi.live({ signal: controller.signal });
        } catch (error) {
            dispatch({ type: "status", status: "error", message: "Live status is unavailable.", error });
            return;
        }
        if (controller.signal.aborted) return;
        const liveStatus = live.live || {};
        const sessionKey = liveStatus.session_key;
        let layout = null;
        if (sessionKey) {
            layout = await raceEngineApi.layout(sessionKey, { signal: controller.signal }).catch(() => null);
        }
        dispatch({ type: "live_ready", layout, live: liveStatus });
        if (live.snapshot) {
            liveBaseRef.current = live.snapshot;
            dispatch({ type: "snapshot", snapshot: live.snapshot });
            clockRef.current.cursor = live.snapshot.clock?.session_seconds ?? 0;
            dispatch({ type: "cursor", cursor: clockRef.current.cursor });
        } else {
            dispatch({ type: "status", status: "empty", message: live.message || "No live session right now." });
        }
        connectStream(controller);
    }, [connectStream]);

    // Initial selection
    useEffect(() => {
        if (initialMode === "live") {
            selectSession(null, "live");
        } else if (initialSessionKey) {
            selectSession(initialSessionKey, "replay");
        }
        return () => {
            if (abortRef.current) abortRef.current.abort();
            if (sseRef.current) sseRef.current.close();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSessionKey, initialMode]);

    // ------------------------------------------------------------ playback clock
    // One authoritative cursor. Every frame: advance it, materialize the state at exactly that time (synchronously,
    // from the decoded checkpoint and patches in memory), publish the state only when a patch or a time-derived
    // status changed, publish the cursor at CURSOR_DISPATCH_HZ or the frame an event becomes current, prefetch
    // ahead, and tick the frame context for the map's interpolated motion. Nothing polls the backend per frame.
    useEffect(() => {
        if (state.mode !== "replay" || !state.playing || !state.manifest) return undefined;
        let frame = null;
        let lastCursorKey = null;
        let lastPrefetchKey = null;
        let lagging = false;
        const manifest = state.manifest;
        clockRef.current.lastFrame = null;
        const eventIndexAfter = (t) => {
            const times = eventTimesRef.current;
            let lo = 0;
            let hi = times.length;
            while (lo < hi) {
                const mid = (lo + hi) >> 1;
                if (times[mid] <= t) lo = mid + 1;
                else hi = mid;
            }
            return lo;
        };
        let nextEventIndex = eventIndexAfter(clockRef.current.cursor);
        const tick = (now) => {
            const clock = clockRef.current;
            if (clock.lastFrame != null) {
                const delta = ((now - clock.lastFrame) / 1000) * stateRef.current.speed;
                clock.cursor = Math.min(manifest.time.end_seconds, clock.cursor + delta);
            }
            clock.lastFrame = now;
            const t = clock.cursor;
            const materializer = materializerRef.current;
            let result = null;
            if (materializer && materializer.manifest.build_id === manifest.build_id) {
                result = materializer.stateAt(t);
                if (result) {
                    publishState(result, t, { force: lagging });
                    lagging = false;
                } else {
                    ensureStateAround(t);
                    if (!lagging) {
                        lagging = true;
                        dispatch({ type: "state_lag", lag: Math.max(0, t - (lastDispatchRef.current.t ?? t)) });
                    }
                }
            }
            const log = playbackLogRef.current;
            if (log) {
                log.samples.push({ wall: now, t, stateT: lastDispatchRef.current.t, appliedThroughS: lastDispatchRef.current.appliedThroughS, lagging, speed: stateRef.current.speed });
                if (log.samples.length > 20000) log.samples.splice(0, log.samples.length - 20000);
            }
            const cursorKey = Math.floor(t * CURSOR_DISPATCH_HZ);
            const eventNow = nextEventIndex < eventTimesRef.current.length && eventTimesRef.current[nextEventIndex] <= t;
            if (eventNow) nextEventIndex = eventIndexAfter(t);
            if (cursorKey !== lastCursorKey || eventNow) {
                lastCursorKey = cursorKey;
                dispatch({ type: "cursor", cursor: t });
            }
            const prefetchKey = Math.floor(t / 5);
            if (prefetchKey !== lastPrefetchKey) {
                lastPrefetchKey = prefetchKey;
                ensureMotionAround(t);
                ensureStateAround(t + STATE_PREFETCH_SECONDS);
            }
            setFrameTick((value) => value + 1);
            if (t >= manifest.time.end_seconds) {
                dispatch({ type: "playing", playing: false });
                return;
            }
            frame = window.requestAnimationFrame(tick);
        };
        frame = window.requestAnimationFrame(tick);
        return () => {
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, [state.mode, state.playing, state.manifest, ensureMotionAround, ensureStateAround, publishState]);

    // Event times, sorted, for cursor publication the frame an event becomes current.
    useEffect(() => {
        eventTimesRef.current = (state.events || []).map((event) => event.session_seconds).filter((value) => value != null).sort((a, b) => a - b);
    }, [state.events]);

    // Developer instrumentation: per-frame cursor/state samples and state transitions (window.__racePlayback).
    useEffect(() => {
        if (!instrument || typeof window === "undefined") return undefined;
        const log = {
            samples: [],
            transitions: [],
            take: () => ({ samples: log.samples.splice(0), transitions: log.transitions.splice(0) }),
            // Independent check at any instant: the cursor, the state the panels display, and the patches due at the
            // cursor that the displayed state does not carry (0 whenever the assets are loaded).
            check: () => {
                const t = clockRef.current.cursor;
                const materializer = materializerRef.current;
                return { t, stateT: lastDispatchRef.current.t, appliedThroughS: lastDispatchRef.current.appliedThroughS, pendingPatches: materializer ? materializer.pendingAt(t) : null, missing: materializer ? materializer.missing(t).length : null };
            },
        };
        playbackLogRef.current = log;
        window.__racePlayback = log;
        return () => {
            playbackLogRef.current = null;
            if (window.__racePlayback === log) delete window.__racePlayback;
        };
    }, [instrument]);

    // Live mode: advance the local render clock between snapshots so motion interpolates smoothly.
    useEffect(() => {
        if (state.mode !== "live" || state.status !== "ready") return undefined;
        let frame = null;
        let last = null;
        const tick = (now) => {
            if (last != null) clockRef.current.cursor += (now - last) / 1000;
            last = now;
            setFrameTick((value) => value + 1);
            frame = window.requestAnimationFrame(tick);
        };
        frame = window.requestAnimationFrame(tick);
        return () => {
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, [state.mode, state.status]);

    // ------------------------------------------------------------- selectors
    const projector = useMemo(() => buildProjector(state.layout), [state.layout]);
    const layoutLengthM = state.layout?.length_m || null;

    const frame = useMemo(() => ({ frameTick, cursorRef: clockRef, motionStore: motionStore.current, telemetryFor: (entryId) => telemetryRef.current.get(entryId) || [] }), [frameTick]);

    const api = useMemo(
        () => ({
            ...state,
            projector,
            layoutLengthM,
            cursorRef: clockRef,
            motionStore: motionStore.current,
            telemetryFor: (entryId) => telemetryRef.current.get(entryId) || [],
            loadCatalog,
            selectSession,
            play: () => dispatch({ type: "playing", playing: true }),
            pause: () => dispatch({ type: "playing", playing: false }),
            togglePlay: () => dispatch({ type: "playing", playing: !stateRef.current.playing }),
            setSpeed: (speed) => dispatch({ type: "speed", speed }),
            seekTo,
            materializeAt: materialize, // read-only: checkpoint + patches at t, the same merge the panels display
            ensureMotionRange: (t0, t1) => {
                // read-only prefetch of motion/telemetry chunks covering [t0, t1] (inspector lap traces)
                const manifest = stateRef.current.manifest;
                if (!manifest) return;
                for (let index = chunkIndexFor(manifest, t0); index <= chunkIndexFor(manifest, t1); index += 1) {
                    fetchChunk("motion", index).catch(() => null);
                    fetchChunk("telemetry", index).catch(() => null);
                }
            },
            stepSeconds: (delta) => seekTo(clockRef.current.cursor + delta),
            jumpToStart: () => seekTo(state.manifest?.time?.race_start_seconds != null ? state.manifest.time.race_start_seconds - 30 : state.manifest?.time?.origin_seconds || 0),
            jumpToFinish: () => {
                const time = state.manifest?.time || {};
                const flag = time.winner_finish_seconds ?? time.chequered_seconds;
                seekTo(flag != null ? flag - 90 : time.end_seconds || 0);
            },
            jumpToEvent: (event) => {
                if (event?.session_seconds != null) seekTo(Math.max(state.manifest?.time?.origin_seconds ?? 0, event.session_seconds - 8));
                if (event?.participants?.length) dispatch({ type: "select_entry", entryId: event.participants[0] });
            },
            selectEntry: (entryId) => dispatch({ type: "select_entry", entryId: stateRef.current.selectedEntryId === entryId ? null : entryId }),
            setSelectedEntry: (entryId) => dispatch({ type: "select_entry", entryId }),
            setCompareEntry: (entryId) => dispatch({ type: "compare_entry", entryId }),
            setFollow: (follow) => dispatch({ type: "follow", follow }),
            returnToLive: () => selectSession(null, "live"),
        }),
        [state, projector, layoutLengthM, loadCatalog, selectSession, seekTo, materialize, fetchChunk]
    );

    return (
        <RaceSessionContext.Provider value={api}>
            <RaceFrameContext.Provider value={frame}>{children}</RaceFrameContext.Provider>
        </RaceSessionContext.Provider>
    );
}

/** Per-frame signals: the animation cursor and motion arrivals. Subscribe only where something is drawn per frame. */
export function useRaceFrame() {
    const value = useContext(RaceFrameContext);
    if (!value) throw new Error("useRaceFrame must be used inside RaceSessionProvider");
    return value;
}

export function useRaceSession() {
    const value = useContext(RaceSessionContext);
    if (!value) throw new Error("useRaceSession must be used inside RaceSessionProvider");
    return value;
}
